// @flow strict

import React from 'react';

import { graphql } from 'gatsby';
import { Grid } from '@material-ui/core';

import { AboutUs } from '~components/about-us/about-us';
import { CategoryImageBanner } from '~components/layout/category-image-banner/category-image-banner';
import { Layout } from '~components/layout/layout';
import { FrontPost } from '~components/blog-elements/front-post';
import { PostPreview } from '~components/blog-elements/post-preview';
import { PageIntro } from '~components/layout/page-intro/page-intro';
import { NewsletterSignup } from '~components/newsletter-signup/newsletter-signup';

import type { Category } from '~types/category';
import type { Alternates, PostNode } from '~types/post';
import { createLinkPath } from '~utils/helpers/create-link-path';

import { withApp } from '../app';

/**
 * Type of component properties.
 */
type CategoryPageProps = {
  data: {
    allMarkdownRemark: {
      nodes: Array<PostNode>,
    },
    categoriesJson: Category,
    site: {
      siteMetadata: { domain: string },
    },
  },

  pageContext: {
    name: string,
    alternates: Alternates,
    canonical: string,
  },
};

/**
 * Template for category page.
 *
 * @param {CategoryPageProps} props - component props
 *
 * @returns {Node}
 */
const CategoryPageTemplate = ({ data, pageContext }: CategoryPageProps) => {
  const { name, alternates } = pageContext;
  const posts = data.allMarkdownRemark.nodes;
  const domain = data.site.siteMetadata.domain;
  const category = data.categoriesJson;
  const { header, banner } = category;

  const [frontPost, ...listPosts] = posts;

  const seoFields = {
    title: name,
    description: category.description,
    canonical: createLinkPath({
      path: pageContext.canonical,
      domain,
      isAbsolute: true,
    }),
    alternates,
  };

  return (
    <Layout seoFields={seoFields} alternates={alternates}>
      <section className="container">
        {header != null ? (
          <PageIntro headline={header.headline} text={header.text} />
        ) : (
          banner != null && (
            <CategoryImageBanner src={banner.src} alt={banner.alt} />
          )
        )}
      </section>

      <div className="container">
        <FrontPost post={frontPost} />
      </div>

      <section className="container">
        <Grid container spacing={4}>
          {listPosts.map((post, index) => {
            return (
              <Grid item md={4} sm={6} xs={12} key={post.id}>
                {' '}
                <PostPreview post={post} hideImage={index % 2 === 0} />{' '}
              </Grid>
            );
          })}
        </Grid>
      </section>

      <div className="container">
        <NewsletterSignup />
      </div>

      <div className="container">
        <AboutUs />
      </div>
    </Layout>
  );
};

export default withApp(CategoryPageTemplate);

// noinspection JSUnusedGlobalSymbols
export const pageQuery = graphql`
  query CategoryQuery(
    $language: String!
    $name: String!
    $dateFormat: String!
  ) {
    categoriesJson(language: { eq: $language }, name: { eq: $name }) {
      ...Category
    }

    allMarkdownRemark(
      sort: { fields: frontmatter___date, order: DESC }
      filter: {
        frontmatter: { language: { eq: $language }, category: { eq: $name } }
        fields: { draft: { eq: false } }
      }
    ) {
      nodes {
        ...PostPreview
        ...FrontPost
      }
    }

    site {
      siteMetadata {
        domain
      }
    }
  }
`;
